import { z } from 'zod';
import { defaultResponse } from '@/lib/generate-rules';
import { toTypedSchema } from '@vee-validate/zod';
import { Ref } from 'vue';

type Comparison = 'before' | 'after';

interface DateValidatorOptions {
  required?: boolean;
  compareTo?: Ref<Date | null>;
  comparison?: Comparison;
  invalidDateMessage?: string;
  requiredMessage?: string;
  comparisonMessage?: string;
}

export function createDateSchema(options: DateValidatorOptions) {
  const {
    required = true,
    compareTo,
    comparison = 'before',
    invalidDateMessage = 'Invalid date',
    requiredMessage = defaultResponse,
    comparisonMessage = comparison === 'before'
      ? 'Date must be before the other date'
      : 'Date must be after the other date',
  } = options;

  return toTypedSchema(
    z.preprocess(
      (val) => {
        if (!val) return null;
        if (val instanceof Date) return val; // already a Date
        if (typeof val === 'string' || typeof val === 'number') {
          // It could be a date string, which it most likely is. not a real date
          // So the comparison wouldnt work.
          const date = new Date(val);
          return isNaN(date.getTime()) ? val : date;
        }
        return val;
      },
      z
        .date({ required_error: requiredMessage, invalid_type_error: invalidDateMessage })
        .nullable()
        .refine((val) => !required || val !== null, { message: requiredMessage })
        .refine(
          (val) => {
            if (!val) return true;
            if (!compareTo?.value) return true;
            return comparison === 'before' ? val <= compareTo.value : val >= compareTo.value;
          },
          { message: comparisonMessage }
        )
    )
  );
}

/** Use examples
 *
 * https://zod.dev/api#dates
 *
 * No comparision requried
 * const optionalDate = createDateSchema({ required: false });
 *
 *
 * Start Date
 * export const startDateSchema = createDateSchema({
 *   compareTo: otherDate,
 *   comparison: 'before',
 *   required: true,
 * });
 *
 *
 * End date
 * const endDateSchema = createDateSchema({
 *   compareTo: startDate,
 *   comparison: 'after',
 *   required: true,
 * });
 */
